import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    TextField,
    Autocomplete,
    Snackbar,
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { DataGrid } from '@mui/x-data-grid';
import { getAssociatedFacilities, getAssociatedFacilityOptions, updateAssociatedFacility } from './associationService';
import { getSiteOptions } from './facilityService';

const AssociationTab = () => {
    const [associations, setAssociations] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [totalRecords, setTotalRecords] = useState(0);
    const [filters, setFilters] = useState({ assetbarcode: '', qrcode: '', facility: '' });
    const [facilityOptions, setFacilityOptions] = useState([]);
    const [siteOptions, setSiteOptions] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [updatedFacility, setUpdatedFacility] = useState('');


    const fetchAssociations = useCallback(async (page, pageSize, filters) => {
        setLoading(true);
        try {
            const { data, pagination } = await getAssociatedFacilities(page, pageSize, filters);
            setAssociations(data);
            setTotalRecords(pagination.totalRecords);
        } catch (error) {
            console.error('Error fetching associations:', error);
            setErrorMessage('Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchFacilityFilters = useCallback(async () => {
        try {
            const facilities = await getAssociatedFacilityOptions();
            setFacilityOptions(facilities);
        } catch (error) {
            console.error('Error fetching facility filters:', error);
            setErrorMessage('Failed to fetch facility filters.');
        }
    }, []);
    // We want to fetchb distinct sites from facility_db as user can move the car to newly  
    // facilities which are not associated yet.  
    const fetchSiteFilters = useCallback(async () => {
        try {
            const sites = await getSiteOptions();
            setSiteOptions(sites);
        } catch (error) {
            console.error('Error fetching site filters:', error);
            setErrorMessage('Failed to fetch site filters.');
        }
    }, []);

    useEffect(() => {
        fetchAssociations(paginationModel.page + 1, paginationModel.pageSize, filters);
    }, [paginationModel, fetchAssociations, filters]);

    useEffect(() => {
        fetchFacilityFilters();
    }, [fetchFacilityFilters]);
    // sites from facility_db
    useEffect(() => {
        fetchSiteFilters();
    }, [fetchSiteFilters]);

    const handlePaginationChange = (model) => setPaginationModel(model);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleCloseSnackbar = () => {
        setSuccessMessage('');
        setErrorMessage('');
    };

    const handleOpenUpdateDialog = (row) => {
        setSelectedRow(row);
        setUpdatedFacility(row.facility); // Pre-fill with the current facility
        setOpenUpdateDialog(true);
    };

    const handleCloseUpdateDialog = () => {
        setOpenUpdateDialog(false);
        setSelectedRow(null);
    };

    const handleUpdateFacility = async () => {
        try {
            // Send the update request
            await updateAssociatedFacility(selectedRow.assetbarcode, updatedFacility);

            // Update the state optimistically
            setAssociations((prev) =>
                prev.map((item) =>
                    item.assetbarcode === selectedRow.assetbarcode
                        ? { ...item, facility: updatedFacility }
                        : item
                )
            );

            setSuccessMessage('Facility updated successfully!');
            setOpenUpdateDialog(false); // Close the dialog after successful update
        } catch (error) {
            console.error('Error updating facility:', error);
            setErrorMessage('Failed to update facility.');
        }
    };

    const columns = [
        { field: 'assetbarcode', headerName: 'Asset Barcode', width: 150 },
        { field: 'qrcode', headerName: 'QR Code', width: 150 },
        { field: 'facility', headerName: 'Facility', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => (
                <IconButton
                    color="primary"
                    onClick={() => handleOpenUpdateDialog(params.row)}
                >
                    <EditIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <Box>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
                <TextField
                    label="Filter by Asset Barcode"
                    variant="outlined"
                    value={filters.assetbarcode}
                    onChange={(e) => handleFilterChange('assetbarcode', e.target.value)}
                    sx={{ width: '250px' }}
                />
                <TextField
                    label="Filter by QR Code"
                    variant="outlined"
                    value={filters.qrcode}
                    onChange={(e) => handleFilterChange('qrcode', e.target.value)}
                    sx={{ width: '250px' }}
                />
                <Autocomplete
                    options={facilityOptions}
                    value={filters.facility}
                    onChange={(e, value) => handleFilterChange('facility', value)}
                    renderInput={(params) => (
                        <TextField {...params} label="Filter by Facility" variant="outlined" sx={{ width: '250px' }} />
                    )}
                />
            </Box>

            {/* DataGrid */}
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <DataGrid
                    rows={associations}
                    columns={columns}
                    pagination
                    paginationMode="server"
                    rowCount={totalRecords}
                    pageSizeOptions={[10, 20, 50]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationChange}
                    loading={loading}
                    getRowId={(row) => row.id}
                />
            </Box>
            <Dialog open={openUpdateDialog} onClose={handleCloseUpdateDialog}>
                <DialogTitle>Update Facility</DialogTitle>
                <DialogContent>
                    <DialogContentText>Update the facility for the selected asset.</DialogContentText>
                    <Autocomplete
                        options={siteOptions}
                        value={updatedFacility}
                        onChange={(e, newValue) => setUpdatedFacility(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Facility"
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUpdateDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            await handleUpdateFacility();
                            handleCloseUpdateDialog();
                        }}
                        color="primary"
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbars */}
            <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert severity="success">{successMessage}</Alert>
            </Snackbar>
            <Snackbar open={!!errorMessage} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert severity="error">{errorMessage}</Alert>
            </Snackbar>
        </Box>
    );
};

export default AssociationTab;
